import React, { useState, useEffect } from 'react';
import BeachCard from './components/BeachCard';
import { BeachData } from './types';
import { getBeachesData, getUserLocation } from './services/api';

const App: React.FC = () => {
  const [beachesData, setBeachesData] = useState<BeachData[]>([]);
  const [filteredBeaches, setFilteredBeaches] = useState<BeachData[]>([]);
  const [sortedBeaches, setSortedBeaches] = useState<BeachData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [maxDistance, setMaxDistance] = useState<number>(500); // Default: show all beaches within 500 miles
  const [sortBy, setSortBy] = useState<'latitude' | 'distance' | 'weather'>('latitude');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get user location
        const position = await getUserLocation();
        const userLat = position.coords.latitude;
        const userLon = position.coords.longitude;

        setUserLocation({ lat: userLat, lon: userLon });

        // Fetch beach data with initial max distance
        const data = await getBeachesData(userLat, userLon, 500);
        // Sort beaches by latitude (northernmost first)
        const sortedData = data.sort((a, b) => b.beach.latitude - a.beach.latitude);
        setBeachesData(sortedData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter beaches based on distance
  useEffect(() => {
    const filtered = beachesData.filter(beach => beach.distance <= maxDistance);
    setFilteredBeaches(filtered);
  }, [beachesData, maxDistance]);

  // Sort filtered beaches
  useEffect(() => {
    const sorted = [...filteredBeaches].sort((a, b) => {
      switch (sortBy) {
        case 'latitude':
          return b.beach.latitude - a.beach.latitude; // North to South
        case 'distance':
          return a.distance - b.distance; // Closest first
        case 'weather':
          return (b.scores?.arrivalScore || b.weatherScore) - (a.scores?.arrivalScore || a.weatherScore); // Best weather first
        default:
          return 0;
      }
    });
    setSortedBeaches(sorted);
  }, [filteredBeaches, sortBy]);

  const handleRefresh = () => {
    if (userLocation) {
      setLoading(true);
      getBeachesData(userLocation.lat, userLocation.lon, 500)
        .then((data) => {
          // Sort beaches by latitude (northernmost first)
          const sortedData = data.sort((a, b) => b.beach.latitude - a.beach.latitude);
          setBeachesData(sortedData);
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          🌊 Loading Oregon coast beaches and weather data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          ❌ {error}
          <br />
          <button onClick={handleRefresh} style={{ marginTop: '10px', padding: '8px 16px' }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <header className="header">
        <h1>🏖️ Oregon Coast Beach Weather</h1>
        <p>Find the perfect beach based on weather conditions and distance from your location</p>

        <div className="controls">
          <div className="distance-filter">
            <label htmlFor="distance-range">
              Max Distance: {maxDistance === 500 ? 'All' : `${maxDistance} miles`}
            </label>
            <input
              id="distance-range"
              type="range"
              min="10"
              max="500"
              step="10"
              value={maxDistance}
              onChange={(e) => setMaxDistance(Number(e.target.value))}
              className="distance-slider"
            />
            <div className="distance-labels">
              <span>10mi</span>
              <span>150mi</span>
              <span>300mi</span>
              <span>500mi (All)</span>
            </div>
          </div>

          <div className="sort-filter">
            <label htmlFor="sort-select">Sort by:</label>
            <select
              id="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'latitude' | 'distance' | 'weather')}
              className="sort-dropdown"
            >
              <option value="latitude">📍 Location (N to S)</option>
              <option value="distance">🚗 Distance (Closest)</option>
              <option value="weather">☀️ Weather (Best)</option>
            </select>
          </div>

          <button onClick={handleRefresh} className="refresh-button">
            🔄 Refresh Data
          </button>
        </div>

        <div className="beach-count">
          Showing {sortedBeaches.length} of {beachesData.length} beaches
        </div>
      </header>

      <div className="beach-list">
        {sortedBeaches.map((beachData, index) => (
          <BeachCard key={`${beachData.beach.name}-${index}`} beachData={beachData} />
        ))}
      </div>

      {sortedBeaches.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
          No beach data available
        </div>
      )}
    </div>
  );
};

export default App;